import isEmpty from "lodash-es/isEmpty"
import isNumber from "lodash-es/isNumber"
import map from "lodash-es/map"

import type { TTFunction } from "$/i18n/i18n"

import type { TSessionInfoParticipant } from "@/3514/components"
import type { TCoachingChatSeedQuestion } from "@/3514/entities/coaching-chat"
import type { TLikertQuestion, TLikertQuestionOption } from "@/3514/entities/likert"
import type {
    IChapterCoachingMomentComponentModel,
    IChapterCoachingMomentSeedQuestionModel,
    ICoachingSessionParticipantModel,
    ISelfDiagnosticQuestionBaseModel,
    ISelfDiagnosticQuestionOptionModel
} from "@/3514/types"
import type { TGetValueFromTranslatedObjectFunction } from "@/hooks"

function mapChapterCoachingMomentSeedQuestionsToChatSeedQuestions(
    componentModel: Partial<IChapterCoachingMomentComponentModel>
): TCoachingChatSeedQuestion[] {
    return isEmpty(componentModel?.answerData?.seedQuestions)
        ? isEmpty(componentModel?.seedQuestions)
            ? []
            : map<IChapterCoachingMomentSeedQuestionModel, TCoachingChatSeedQuestion>(
                  componentModel.seedQuestions,
                  ({ question, questionUuid }: IChapterCoachingMomentSeedQuestionModel): TCoachingChatSeedQuestion => ({
                      questionUuid,
                      question,
                      answer: String()
                  })
              )
        : map<
              IChapterCoachingMomentSeedQuestionModel & {
                  answer: string
              },
              TCoachingChatSeedQuestion
          >(
              componentModel.answerData.seedQuestions,
              ({
                  question,
                  questionUuid,
                  answer
              }: IChapterCoachingMomentSeedQuestionModel & {
                  answer: string
              }): TCoachingChatSeedQuestion => ({ questionUuid, question, answer })
          )
}

function mapSelfAssessmentQuestionsToLikertQuestions<
    T extends Function = TTFunction | TGetValueFromTranslatedObjectFunction
>(
    questions: ISelfDiagnosticQuestionBaseModel[],
    translateFunc: T,
    config: { order: number; withPrompt: boolean; withFreeResponse: boolean } = {
        order: undefined,
        withPrompt: true,
        withFreeResponse: true
    }
): TLikertQuestion[] {
    return isEmpty(questions)
        ? []
        : map<ISelfDiagnosticQuestionBaseModel, TLikertQuestion>(
              questions,
              (model: ISelfDiagnosticQuestionBaseModel): TLikertQuestion => ({
                  initialState: {
                      id: model.uuid,
                      content: translateFunc(model.question),
                      order: isNumber(config.order) ? config.order : model.order,
                      isNotApplicable: config.withPrompt ? model.isNotApplicable : false,
                      notApplicableLabel: config.withPrompt ? translateFunc(model.notApplicableLabel) : String(""),
                      notApplicablePrompt: config.withPrompt ? translateFunc(model.notApplicableLabel) : String(""),
                      options: isEmpty(model.options)
                          ? []
                          : map<ISelfDiagnosticQuestionOptionModel, TLikertQuestionOption>(
                                model.options,
                                (option: ISelfDiagnosticQuestionOptionModel, index: number): TLikertQuestionOption => ({
                                    index,
                                    uuid: option.uuid,
                                    wasSelected: false,
                                    label: translateFunc(option.response),
                                    ...(option.isFreeResponseAllowed &&
                                        config.withFreeResponse && {
                                            isFreeResponseAllowed: true,
                                            freeResponsePrompt: translateFunc(option.freeResponsePrompt),
                                            freeResponsePlaceholder: translateFunc(option.freeResponsePrompt)
                                        })
                                })
                            )
                  },
                  submittedState: null
              })
          )
}

function mapCoachingSessionsParticipantsToSessionInfoParticipants(
    modelParticipants: ICoachingSessionParticipantModel[]
): TSessionInfoParticipant[] {
    return isEmpty(modelParticipants)
        ? []
        : map<ICoachingSessionParticipantModel, TSessionInfoParticipant>(
              modelParticipants,
              (p: ICoachingSessionParticipantModel): TSessionInfoParticipant => ({
                  id: p.id,
                  firstName: p.firstName,
                  lastName: p.lastName,
                  rsvpStatus: p.invitationStatus,
                  attendanceStatus: p.attendanceStatus,
                  extraPartyRole: null,
                  isExtraParty: p.isGuest,
                  isGuest: p.isGuest,
                  isHost: false,
                  photo: p.photo
              })
          )
}

export {
    mapChapterCoachingMomentSeedQuestionsToChatSeedQuestions,
    mapSelfAssessmentQuestionsToLikertQuestions,
    mapCoachingSessionsParticipantsToSessionInfoParticipants
}
